/**
 * Service Worker 侧推送事件判定（任务 18b）。
 *
 * 纯逻辑，有单测；`sw.ts` 只做薄接线（同 policy.ts / updateModel.ts 的分工）。
 * 本文件也被主线程引用（`push/notificationClicks.ts` 要用同一个消息协议常量），
 * 所以**不得引用 `self` / ServiceWorkerGlobalScope**，只处理数据。
 *
 * 两件事：
 * 1. `push` 事件的载荷 → 通知计划（`pushNotificationPlan`）。载荷**恒有兜底**：
 *    Web Push 规范要求收到推送必须展示用户可见的通知，否则浏览器会扣「预算」
 *    并可能吊销订阅——所以载荷解析失败时也要弹一条泛化通知，不能静默 return。
 * 2. `notificationclick` → 落点决策（`notificationClickPlan`）：优先 focus 已经
 *    开着的窗口并让它**站内路由**（postMessage，不走 client.navigate——那会整页
 *    重载，SPA 冷启成本全白付）；没有窗口才 `openWindow`。
 *
 *    ⚠️ **冷启动刻意开首页而不是深链**（21ak，人工报「从 push 点进会话，返回是
 *    空白的」）：`openWindow('/?open=dm:101')` → 应用在首页起来（会话列表真的挂载
 *    并走完它自己的首屏数据链）→ 主线程读走 query 再 `openChat` 推一层。
 *    这样历史栈是**真正导航出来的** [首页, 会话页]，返回就是普通的出栈。
 *    深链 `/chat/dm/101` 虽然也会被 stackForPath 垫成两层，但会话列表在
 *    「返回」那一刻才第一次挂载——它的首屏链没跑过，看起来就是一片空白。
 *
 * 载荷字段由本项目定义（后端按 `docs/webpush-backend.md` 实现，18c 交付）：
 *   { conversationKey: "dm:101" | "group:77", title, body, icon?, unread? }
 * 刻意不让后端下发 URL：路由形状是前端的事（`notifyTargetUrl` 单点），
 * 后端只给会话键，改路由不用动后端。
 */
import { notifyTagOf, notifyTargetUrl } from '~/push/notifyDecision';
import { BRAND_NAME } from '~/brand';

/** SW → 页面的消息协议：点击通知后让已开着的页面站内跳到会话 */
export const SW_OPEN_CHAT = 'lhc/open-chat';

export interface SwOpenChatMessage {
  readonly type: typeof SW_OPEN_CHAT;
  /** 站内路径（`/chat/dm/101`），页面侧用 parseRoute 还原 */
  readonly url: string;
}

export function swOpenChatMessage(url: string): SwOpenChatMessage {
  return { type: SW_OPEN_CHAT, url };
}

/** 通知的 data 字段（点击时唯一可用的上下文） */
export interface PushClickData {
  readonly url: string;
  readonly conversationKey: string;
}

export interface PushNotificationPlan {
  readonly title: string;
  readonly body: string;
  readonly tag: string;
  /** 空串 = 不带图标 */
  readonly icon: string;
  readonly data: PushClickData;
  /** 同 tag 的通知被替换时仍然提醒一次（否则静默替换，用户错过新消息） */
  readonly renotify: boolean;
}

const FALLBACK_TITLE = BRAND_NAME;
const FALLBACK_BODY = '您有新消息';
/** 无会话键时的折叠标签与落点：全部收敛成一条，点开进首页 */
const FALLBACK_TAG = 'lhc-chat';
const HOME_URL = '/';

function str(src: Record<string, unknown>, key: string): string {
  const v = src[key];
  return typeof v === 'string' ? v : '';
}

/** 只认 `dm:<数字>` / `group:<数字>`：载荷来自网络，不能直接拼进 URL */
export function isConversationKey(value: unknown): value is string {
  return typeof value === 'string' && /^(dm|group):[1-9][0-9]{0,18}$/.test(value);
}

/**
 * 推送载荷 → 通知计划。坏载荷（非对象 / 无会话键 / 无文案）逐字段退到兜底，
 * **永不返回 null**（见文件头：必须展示通知）。
 */
export function pushNotificationPlan(raw: unknown): PushNotificationPlan {
  const src: Record<string, unknown> =
    typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const key = src['conversationKey'];
  const hasKey = isConversationKey(key);
  return {
    title: str(src, 'title') || FALLBACK_TITLE,
    body: str(src, 'body') || FALLBACK_BODY,
    tag: hasKey ? notifyTagOf(key) : FALLBACK_TAG,
    icon: /^https:\/\//i.test(str(src, 'icon')) ? str(src, 'icon') : '',
    data: {
      url: hasKey ? notifyTargetUrl(key) : HOME_URL,
      conversationKey: hasKey ? key : '',
    },
    renotify: true,
  };
}

/** 冷启动落点的 query 参数名（主线程 `push/coldStartOpen.ts` 读它） */
export const COLD_START_PARAM = 'open';

/** `/chat/dm/101` → `dm:101`；不是会话路径返回空串 */
function keyFromChatPath(url: string): string {
  const m = /^\/chat\/(dm|group)\/([1-9][0-9]{0,18})(?:[/?#]|$)/.exec(url);
  return m ? `${m[1]}:${m[2]}` : '';
}

/**
 * 通知 data → **冷启动**落点（见文件头：开首页 + query 带目标，不深链）。
 * conversationKey 缺失时（历史通知残留）退一步从 data.url 里反解，
 * 都拿不到就只开首页。
 */
export function coldStartUrlOf(data: unknown): string {
  const src: Record<string, unknown> =
    typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
  const raw = src['conversationKey'];
  const key = isConversationKey(raw) ? raw : keyFromChatPath(clickUrlOf(data));
  return key ? `${HOME_URL}?${COLD_START_PARAM}=${encodeURIComponent(key)}` : HOME_URL;
}

/** 通知 data 还原落点（旧版本通知残留 / 被篡改时退首页） */
export function clickUrlOf(data: unknown): string {
  if (typeof data !== 'object' || data === null) return HOME_URL;
  const url = (data as Record<string, unknown>)['url'];
  // 只接受站内绝对路径，拒绝 `//evil.com` 这类协议相对 URL
  if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) return HOME_URL;
  return url;
}

/** `clients.matchAll({ type: 'window' })` 的最小形状 */
export interface ClickClient {
  readonly id: string;
  readonly focused: boolean;
  readonly visible: boolean;
}

export type NotificationClickPlan =
  | { readonly kind: 'focus'; readonly clientId: string; readonly url: string }
  | { readonly kind: 'open'; readonly url: string };

/**
 * 落点：有窗口就复用（焦点窗口 > 可见窗口 > 第一个），否则开新窗口。
 * 复用时附带站内路径 `url`，由 sw.ts focus 后 postMessage 给页面做站内跳转；
 * 开新窗口用 `coldStartUrl`（首页 + query，见文件头）——两者**刻意不同**。
 * coldStartUrl 省略时退回 url（老调用方/单测的兼容形态）。
 */
export function notificationClickPlan(
  clients: readonly ClickClient[],
  url: string,
  coldStartUrl: string = url,
): NotificationClickPlan {
  const target =
    clients.find((c) => c.focused) ?? clients.find((c) => c.visible) ?? clients[0] ?? null;
  return target ? { kind: 'focus', clientId: target.id, url } : { kind: 'open', url: coldStartUrl };
}
